/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Pencil1Icon, TrashIcon, ReloadIcon, ArrowRightIcon } from '@/shared/ui/radix-icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/components/shadcn/dialog';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isVisitEvent } from '@features/agenda/utils/visit-event';
import { eventColor } from './event-color';
import { isAllDayEvent } from './time-grid';
import { calendarDisplayName } from '@features/agenda/utils/calendar-display';
import { Text, UnstyledButton } from '@/shared/ui';

interface EventPreviewDialogProps {
  open: boolean;
  event: ExpandedEvent;
  calendars: Calendar[];
  canEdit: boolean;
  onEdit: (event: ExpandedEvent) => void;
  onDelete: (event: ExpandedEvent) => void;
  onViewVisit: (event: ExpandedEvent) => void;
  onClose: () => void;
}

/**
 * Formats the event time window as `DD/MM/YYYY · HH:mm – HH:mm`, or as the
 * all-day label when the event spans the whole day.
 * @param event - The expanded event.
 * @param allDayLabel - Translated "all day" label.
 * @returns The formatted date/time span string.
 */
const formatWhen = (event: ExpandedEvent, allDayLabel: string): string => {
  const start = dayjs(event.start_at_utc);
  const end = dayjs(event.end_at_utc);
  const day = start.format('DD/MM/YYYY');
  if (isAllDayEvent(event)) return `${day} · ${allDayLabel}`;
  return `${day} · ${start.format('HH:mm')} às ${end.format('HH:mm')}`;
};

/**
 * Read-only event preview. Surfaces the title, schedule, calendar, description
 * and recurrence, with Edit/Delete actions gated by permission.
 *
 * @param props Event, calendars, permission flag and handlers.
 * @returns Rendered event preview dialog.
 */
export const EventPreviewDialog: React.FC<EventPreviewDialogProps> = ({
  open,
  event,
  calendars,
  canEdit,
  onEdit,
  onDelete,
  onViewVisit,
  onClose,
}) => {
  const { t } = useTranslation('features/agenda');
  const isVisit = isVisitEvent(event);
  const color = eventColor(event, calendars);
  const calendar = calendars.find((c) => c.calendar_id === event.calendar_id);
  const calendarName = isVisit
    ? t('sidebar.visits')
    : calendar
      ? calendarDisplayName(calendar, t)
      : t('preview.unknownCalendar');
  const recurring =
    Array.isArray(event.recurrence) && event.recurrence.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-(--space-2)">
            <Text component="span"
              aria-hidden="true"
              className="h-3 w-3 flex-shrink-0 rounded-full"
              style={{ background: color }}
            />
            {event.title}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-(--space-3)" style={{ fontSize: 'var(--text-small)' }}>
          <div className="flex flex-col gap-(--space-0-5)">
            <Text component="span" className="text-xs font-semibold text-[var(--text-secondary)]">
              {t('preview.when')}
            </Text>
            <Text component="span" className="text-[var(--text-primary)]">
              {formatWhen(event, t('drawer.all_day'))}
            </Text>
          </div>
          <div className="flex flex-col gap-(--space-0-5)">
            <Text component="span" className="text-xs font-semibold text-[var(--text-secondary)]">
              {t('preview.calendar')}
            </Text>
            <Text component="span" className="text-[var(--text-primary)]">{calendarName}</Text>
          </div>
          {recurring && (
            <Text component="span" className="inline-flex w-fit items-center gap-(--space-1) rounded-(--radius-lg) bg-[var(--surface-secondary)] px-(--space-2) py-(--space-1) text-xs text-[var(--text-secondary)]">
              <ReloadIcon />
              {t('preview.recurring')}
            </Text>
          )}
          <Text className="whitespace-pre-wrap text-[var(--text-secondary)]">
            {event.description?.trim() || t('preview.no_description')}
          </Text>
        </div>
        <DialogFooter className="!justify-between">
          {canEdit ? (
            <UnstyledButton
              type="button"
              onClick={() => onDelete(event)}
              className="inline-flex items-center gap-(--space-1-5) rounded-(--radius-lg) border border-[var(--border-subtle)] px-(--space-4) py-(--space-2) text-[var(--status-error-text)] transition-colors hover:bg-[var(--status-error-text)]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--status-error-text)] focus-visible:ring-offset-1"
              style={{ fontSize: 'var(--text-small)' }}
            >
              <TrashIcon />
              {t('preview.delete')}
            </UnstyledButton>
          ) : (
            <Text component="span" />
          )}
          {canEdit && (
            <UnstyledButton
              type="button"
              onClick={() => onEdit(event)}
              className="inline-flex items-center gap-(--space-1-5) rounded-(--radius-lg) px-(--space-4) py-(--space-2) font-semibold text-[var(--button-primary-text)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
              style={{ background: 'var(--seed-base)', fontSize: 'var(--text-small)' }}
            >
              <Pencil1Icon />
              {t('preview.edit')}
            </UnstyledButton>
          )}
          {isVisit && (
            <UnstyledButton
              type="button"
              onClick={() => onViewVisit(event)}
              className="ml-auto inline-flex items-center gap-(--space-1-5) rounded-(--radius-lg) px-(--space-4) py-(--space-2) font-semibold text-[var(--button-primary-text)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
              style={{ background: 'var(--seed-base)', fontSize: 'var(--text-small)' }}
            >
              {t('preview.view_visit')}
              <ArrowRightIcon />
            </UnstyledButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
